// src/services/dishes.ts
//
// Dish (meal dish item) service. Backs the dish dashboard list and the
// Add/Edit dish flow.

import { apiFetch } from "../Component/api/apiFetch";
import { API_URLS } from "../Component/api/url";
import { handleJson } from "./http";

export const getDishDashboardList = (
  filter: "" | "active" | "archive" = "",
  createdBy: string | number = "",
  search: string = "",
  page: number = 1,
  size: number = 10,
): Promise<any> =>
  handleJson(
    apiFetch(
      API_URLS.GET_LIST_IN_DISH_DASHBOARD(filter, createdBy, search, page, size),
      { method: "GET" },
    ),
  );

// Fetch the full detail record for a single dish. Backs the View / Edit
// pages — the list endpoint only returns the tabular columns, so the
// detail-only fields (measurements, cooking-fat bands, ingredients, recipe,
// history, micronutrients) live behind this call.
export const getDishDetails = (dish_id: string | number): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_DISH_DETAILS(dish_id), { method: "GET" }),
  );

// Response from CHECK_DISH_NAME_EXISTS. When `exists` is true the caller
// renders an inline "already exists" error and a View link that navigates
// to `matched_dish_id`. `matched_field` distinguishes a name-vs-tag match,
// and `matched_tags` is populated for tag matches.
export interface CheckDishNameExistsResponse {
  success?: number | string | boolean;
  exists?: boolean;
  matched_field?: string;
  matched_dish_id?: number;
  matched_dish_name?: string;
  matched_tags?: string[];
  message?: string;
}

// Check whether a dish name (or one of its tags) already matches an
// existing dish. Backs the inline duplicate-name check on the Add / Edit
// dish form — fired on debounce after the user types a name.
export const checkDishNameExists = (
  dish_name: string,
): Promise<CheckDishNameExistsResponse> =>
  handleJson(
    apiFetch(API_URLS.CHECK_DISH_NAME_EXISTS(dish_name), {
      method: "GET",
    }),
  );

// Archive (is_hide: true) or restore (is_hide: false) a dish. The backend
// distinguishes archive vs. restore by which reason field is present —
// `hide_reason` for archive, `restore_reason` for restore — so only the
// field relevant to the action is sent. Unlike the ingredient endpoint,
// this one wants `nutritionist_id` in the body (the ingredient side derives
// it from the Bearer token).
export const archiveOrRestoreDish = (params: {
  dish_id: number;
  is_hide: boolean;
  reason: string;
  nutritionist_id: number | string;
}): Promise<any> => {
  const { dish_id, is_hide, reason, nutritionist_id } = params;
  const body: Record<string, unknown> = { dish_id, is_hide, nutritionist_id };
  if (is_hide) {
    body.hide_reason = reason;
  } else {
    body.restore_reason = reason;
  }
  return handleJson(
    apiFetch(API_URLS.ARCHIVE_OR_RESTORE_DISH, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
};

// Single unit-conversion entry sent with CREATE_DISH. The dish endpoint
// keys off the unit string ("cup (small)") plus its base type ("gm" / "ml"),
// unlike CREATE_INGREDIENT which uses the numeric `unit_id`.
export interface CreateDishUnit {
  measure_unit_type: string;
  measure_gram: number;
  measure_type: string;
}

// Single cooking-fat band sent with CREATE_DISH when `is_dynamic_fat` is true.
// The min/max quantity brackets define which band applies for a given serving.
export interface CreateDishCookingFat {
  min_qty: number;
  max_qty: number;
  cooking_fat_value: number;
}

// Single recipe ingredient row for CREATE_DISH. Only sent when the dish has
// a composed recipe. `food_id` points at an existing ingredient in the
// food library; the row's quantity/unit are the amount used in this dish.
export interface CreateDishRecipeIngredient {
  food_id: number;
  quantity: number;
  units: string;
}

// Payload for CREATE_DISH. Field names match the multipart form the backend
// expects one-for-one; conversion rows, cooking-fat bands, and recipe
// ingredients are JSON-stringified when appended to FormData. `tags` is
// serialised as a comma-separated string (backend does not use JSON here).
//
// `micro` carries the micro-nutrient fields keyed by backend snake_case
// names (vitamin_a, thiamine_b1, ...). All 29 keys are appended (empty
// string when unfilled) so the backend receives a consistent shape.
export interface CreateDishPayload {
  dish_name: string;
  dish_units: string;
  dish_base_quantity: number;
  dish_gl: number;
  dish_cals: number;
  dish_AAFAdj_protein: number;
  dish_gross_protein: number;
  dish_carbs: number;
  net_carbs: number;
  category: string;
  type: "VEG" | "NON_VEG";
  tags: string[];
  fibre?: number | "";
  dish_ingredients_fat: number;
  is_dynamic_fat: boolean;
  units: CreateDishUnit[];
  cooking_fats?: CreateDishCookingFat[];
  recipe_method?: string;
  ingredients?: CreateDishRecipeIngredient[];
  micro?: Record<string, string>;
  dish_image?: File | null;
}

// UPDATE_DISH accepts the same fields as CREATE_DISH plus a `dish_id`
// identifying the target record (the endpoint does not take an id in the
// URL). Everything else — units, cooking fats, recipe, micros, image —
// rides in the same multipart form.
export type UpdateDishPayload = CreateDishPayload & { dish_id: number | string };

// Serialise the dish payload into the multipart form the backend expects.
const buildDishFormData = (
  payload: CreateDishPayload | UpdateDishPayload,
): FormData => {
  const form = new FormData();
  if ("dish_id" in payload && payload.dish_id !== undefined && payload.dish_id !== "") {
    form.append("dish_id", String(payload.dish_id));
  }
  form.append("dish_name", payload.dish_name);
  form.append("dish_units", payload.dish_units);
  form.append("dish_base_quantity", String(payload.dish_base_quantity));
  form.append("dish_gl", String(payload.dish_gl));
  form.append("dish_cals", String(payload.dish_cals));
  form.append("dish_AAFAdj_protein", String(payload.dish_AAFAdj_protein));
  form.append("dish_gross_protein", String(payload.dish_gross_protein));
  form.append("dish_carbs", String(payload.dish_carbs));
  form.append("net_carbs", String(payload.net_carbs));
  form.append("category", payload.category);
  form.append("type", payload.type);
  // Tags ride as a comma-separated string (e.g. "Pindi chole, kulche"); an
  // empty tags array is still sent as an empty field so the backend sees
  // a consistent shape.
  form.append("tags", payload.tags.join(", "));
  if (payload.fibre !== undefined && payload.fibre !== "") {
    form.append("fibre", String(payload.fibre));
  } else if (payload.fibre === "") {
    form.append("fibre", "");
  }
  form.append("dish_ingredients_fat", String(payload.dish_ingredients_fat));
  form.append("is_dynamic_fat", String(payload.is_dynamic_fat));
  form.append("units", JSON.stringify(payload.units));
  // Cooking fat bands ride only when the dish opts into dynamic fat — the
  // backend ignores the field otherwise.
  if (payload.is_dynamic_fat && payload.cooking_fats && payload.cooking_fats.length > 0) {
    form.append("cooking_fats", JSON.stringify(payload.cooking_fats));
  }
  if (payload.recipe_method !== undefined && payload.recipe_method !== "") {
    form.append("recipe_method", payload.recipe_method);
  }
  if (payload.ingredients && payload.ingredients.length > 0) {
    form.append("ingredients", JSON.stringify(payload.ingredients));
  }
  // Micro-nutrient fields — always all 29 keys so unfilled micros round-trip
  // as empty strings, matching the ingredient payload contract.
  if (payload.micro) {
    Object.entries(payload.micro).forEach(([key, value]) => {
      form.append(key, value ?? "");
    });
  }
  if (payload.dish_image) {
    form.append("dish_image", payload.dish_image);
  }
  return form;
};

// Create a new dish. Uses multipart/form-data so the optional dish photo
// can ride along with the text fields — apiFetch already skips setting
// Content-Type when the body is FormData, so the browser picks the correct
// boundary automatically.
export const createDish = (payload: CreateDishPayload): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.CREATE_DISH, {
      method: "POST",
      body: buildDishFormData(payload),
    }),
  );

// Update an existing dish. Same multipart shape as create; the target
// record is identified by `dish_id` inside the form (not in the URL).
export const updateDish = (payload: UpdateDishPayload): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.UPDATE_DISH, {
      method: "POST",
      body: buildDishFormData(payload),
    }),
  );

// One per-row failure from a bulk upload. Only populated when the sheet
// parses cleanly but some rows can't be applied (e.g. Modify referencing
// a dish that doesn't exist). Matches the ingredient shape so
// BulkUploadResultView can render either response unchanged — the label
// column is called `item_name` on both endpoints.
export interface UploadDishBulkErrorRow {
  sheet: string;
  row_number: number;
  item_name: string;
  error: string;
}

// One column-name mismatch when the sheet's header row doesn't match what
// the backend expects. Only populated on success === "0" responses.
export interface UploadDishBulkHeaderMismatch {
  column: string;
  expected: string;
  found: string;
  issue: string;
}

// UPLOAD_DISH_BULK_UPLOAD returns one of three shapes:
//   1. success "1" clean — added/updated counts, empty error_rows.
//   2. success "1" partial — some rows landed, others failed (error_rows populated).
//   3. success "0" header mismatch — no rows applied; expected/found headers +
//      header_mismatches describe the diff for the user to fix the sheet.
// All fields are optional so a single interface covers every shape. The
// shape mirrors UploadIngredientBulkResponse so BulkUploadResultView can
// render the response unchanged.
export interface UploadDishBulkResponse {
  success: string;
  message: string;
  added_count?: number;
  updated_count?: number;
  added_items?: string[];
  updated_items?: string[];
  skipped_count?: number;
  skipped_rows?: unknown[];
  error_count?: number;
  error_rows?: UploadDishBulkErrorRow[];
  expected_headers?: string[];
  found_headers?: string[];
  header_mismatches?: UploadDishBulkHeaderMismatch[];
}

// POST the picked Excel file as multipart/form-data. The backend
// distinguishes "add new" vs. "modify existing" purely by matching row
// keys against existing records — the same endpoint serves both the
// Import new and Modify existing flows in the UI (matches the ingredient
// contract). The dish endpoint keys its counts as `dish_added_count` /
// `dish_added` etc. — remap them here so BulkUploadResultView (which is
// shared with the ingredient flow) reads the same field names on both.
export const uploadDishBulkUpload = async (
  nutritionist_id: string | number,
  file: File,
): Promise<UploadDishBulkResponse> => {
  const form = new FormData();
  form.append("nutritionist_id", String(nutritionist_id));
  form.append("file", file);
  const raw = await handleJson<any>(
    apiFetch(API_URLS.UPLOAD_DISH_BULK_UPLOAD, {
      method: "POST",
      body: form,
    }),
  );
  return {
    ...raw,
    success: String(raw?.success ?? ""),
    message: raw?.message ?? "",
    added_count: raw?.added_count ?? raw?.dish_added_count,
    updated_count: raw?.updated_count ?? raw?.dish_updated_count,
    skipped_count: raw?.skipped_count ?? raw?.dish_skipped_count,
    added_items: raw?.added_items ?? raw?.dish_added,
    updated_items: raw?.updated_items ?? raw?.dish_updated,
    skipped_rows: raw?.skipped_rows ?? raw?.dish_skipped,
  };
};

// Re-post the same sheet with ?download_errors=true and stream the
// annotated Excel response back to the browser. Same multipart shape
// as the original upload — the query flag alone tells the backend to
// return the file (errors highlighted / column mismatches flagged)
// instead of the JSON summary, for the user to fix and re-upload.
// Mirrors downloadIngredientBulkUploadErrors on the ingredient side.
export const downloadDishBulkUploadErrors = async (
  nutritionist_id: string | number,
  file: File,
): Promise<void> => {
  const form = new FormData();
  form.append("nutritionist_id", String(nutritionist_id));
  form.append("file", file);
  const response = await apiFetch(
    API_URLS.UPLOAD_DISH_BULK_UPLOAD_DOWNLOAD_ERRORS,
    { method: "POST", body: form },
  );
  if (!response.ok) {
    throw new Error(`Failed to download error file (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename =
    filenameFromContentDisposition(disposition) ??
    `${file.name.replace(/\.xlsx?$/i, "")}_errors.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

// Pull a filename out of a Content-Disposition header. Handles both the
// plain `filename="foo.xlsx"` form and the RFC 5987 `filename*=UTF-8''foo.xlsx`
// form. Returns null when no filename is present so callers can fall back.
const filenameFromContentDisposition = (header: string): string | null => {
  const starMatch = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (starMatch) {
    try {
      return decodeURIComponent(starMatch[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // Fall through to the plain form.
    }
  }
  const plainMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  return plainMatch ? plainMatch[1].trim() : null;
};

// Download the dish bulk-upload Excel template and trigger a browser save.
// The endpoint streams the file directly, so we grab the response as a
// Blob, materialise it via an object URL, and click a synthesised <a
// download> to save it. `variant` maps to the flow the user picked from
// the Bulk upload dropdown ("normal" = Import new, "rename" = Modify).
export const downloadDishBulkUploadTemplate = async (
  variant: "normal" | "rename",
): Promise<void> => {
  const response = await apiFetch(
    API_URLS.DOWNLOAD_DISH_BULK_UPLOAD_TEMPLATE(variant),
    { method: "GET" },
  );
  if (!response.ok) {
    throw new Error(`Failed to download template (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename =
    filenameFromContentDisposition(disposition) ??
    `dish_bulk_upload_${variant}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
